import { useEffect, useState, type ReactNode } from "react";

import { economicsService } from "@/services/economicsService";
import { handleApiError } from "@/services/api";
import { inrWhole as inr, inrOrDash as inr2, flockMarginPerEgg, signedInr, marginToneClass } from "@/lib/utils";
import { useAuthStore } from "@/store/useAuthStore";
import TargetVarianceBadge from "@/components/common/TargetVarianceBadge";
import type { FlockEconomics } from "@/types/models";
import CostWaterfall from "./CostWaterfall";

// Whole days between two bare UTC "YYYY-MM-DD" dates. Anchor both at UTC midnight
// so the staleness nudge is timezone-stable, and measure against the report
// window's end (not "now") so a historical window isn't wrongly flagged stale.
function daysBetween(fromIso: string, toIso: string): number {
  const a = new Date(fromIso + "T00:00:00Z").getTime();
  const b = new Date(toIso + "T00:00:00Z").getTime();
  return Math.floor((b - a) / 86_400_000);
}

// Margin/egg computed from the SAME paise-rounded realized and cost the card
// shows, so "realized − cost" always reconciles with the displayed margin (the
// backend's 3-dp margin could otherwise read a paisa off the 2-dp figures).
// All-in basis (cost/egg includes rearing) — see the footnote.
function MarginPerEgg({ realized, cost }: { realized: number; cost: number }) {
  const m = flockMarginPerEgg(realized, cost);
  return (
    <>
      {" "}
      &middot; margin <span className={`font-semibold ${marginToneClass(m)}`}>{signedInr(m)}</span>/egg
    </>
  );
}

// HDP context: the current lay rate (over producing days), the break-even lay
// rate when a realized price exists, and always the logging coverage + a
// staleness nudge — so the number is never shown as more current/complete than
// the egg logging behind it. Rendered whenever there's a current HDP; break-even
// is optional (needs a realized price).
function HdpBanner({
  current,
  breakEven,
  target,
  standard,
  standardWeek,
  standardSource,
  producingDays,
  throughDate,
  windowEnd,
  feedEstimated,
  costIncomplete,
}: {
  current: number;
  breakEven: number | null;
  target: number | null | undefined;
  standard: number | null | undefined;
  standardWeek: number | null | undefined;
  standardSource?: "breed" | "farm" | "default" | null;
  producingDays: number;
  throughDate: string | null;
  windowEnd: string;
  feedEstimated: boolean;
  costIncomplete: boolean;
}) {
  // Order matters: a flock at/above break-even is healthy even if break-even
  // itself exceeds 100% — only call it "unviable" when it's actually short.
  const healthy = breakEven !== null && current >= breakEven;
  const unviable = breakEven !== null && !healthy && breakEven > 100;
  const tone =
    breakEven === null
      ? "bg-gray-50 text-gray-600 dark:bg-white/[0.03] dark:text-gray-300"
      : healthy
        ? "bg-success-50 text-success-700 dark:bg-success-500/10 dark:text-success-400"
        : "bg-error-50 text-error-700 dark:bg-error-500/10 dark:text-error-400";
  const staleDays = throughDate !== null ? daysBetween(throughDate, windowEnd) : 0;
  return (
    <div className={`mb-4 rounded-lg px-3 py-2 text-xs ${tone}`}>
      {breakEven === null ? (
        costIncomplete ? (
          <>
            Current HDP <span className="font-semibold">{current.toFixed(1)}%</span> &mdash; feed cost isn&rsquo;t
            captured, so break-even can&rsquo;t be shown.
          </>
        ) : (
          <>
            Current HDP <span className="font-semibold">{current.toFixed(1)}%</span> &mdash; no realized egg price yet,
            so break-even can&rsquo;t be shown.
          </>
        )
      ) : healthy ? (
        <>
          Break-even HDP <span className="font-semibold">{breakEven.toFixed(1)}%</span> &middot; currently{" "}
          <span className="font-semibold">{current.toFixed(1)}%</span> &mdash;{" "}
          <span className="font-semibold">{(current - breakEven).toFixed(1)} pt</span> cushion before eggs stop
          covering feed + overhead.
        </>
      ) : unviable ? (
        <>
          Break-even needs <span className="font-semibold">{breakEven.toFixed(1)}%</span> HDP &mdash; above 100%:
          at current costs, eggs can&rsquo;t cover feed + overhead at any lay rate.
        </>
      ) : (
        <>
          Below break-even: eggs cover feed + overhead only at{" "}
          <span className="font-semibold">{breakEven.toFixed(1)}%</span> HDP, but the flock is at{" "}
          <span className="font-semibold">{current.toFixed(1)}%</span>.
        </>
      )}
      {target != null && (
        <span className="mt-1 block text-gray-600 dark:text-gray-300">
          <TargetVarianceBadge actual={current} target={target} metric="hdp" />
        </span>
      )}
      {standard != null && standard > 0 && (
        <span className="mt-1 block text-gray-600 dark:text-gray-300">
          <TargetVarianceBadge actual={current} target={standard} metric="hdp" label={standardLabel(standardSource)} />
          {standardWeek != null && <span className="text-gray-400 dark:text-gray-500"> (wk {standardWeek})</span>}
        </span>
      )}
      <span className="mt-1 block text-gray-500 dark:text-gray-400">
        HDP over {producingDays} day{producingDays === 1 ? "" : "s"} of egg logs
        {throughDate !== null && <> through {throughDate}</>}
        {feedEstimated && breakEven !== null && <> &middot; feed rate estimated</>}
        {staleDays > 10 && (
          <span className="font-medium"> &middot; no eggs logged in {staleDays} days &mdash; may be out of date</span>
        )}
      </span>
    </div>
  );
}

// standardLabel names the curve a figure is measured against, so a variance can
// be read as either "below your own strain's guide" or "below a generic default
// we shipped" — very different statements (3b.2).
function standardLabel(source: "breed" | "farm" | "default" | null | undefined): string {
  switch (source) {
    case "breed":
      return "breed standard";
    case "farm":
      return "farm standard";
    case "default":
      return "generic standard";
    default:
      // Absent only on a payload cached from before the source existed. Claiming
      // any of the three would be a guess, so say the neutral thing.
      return "standard";
  }
}

// The mortality tile can carry two references at once — the owner's target and the
// breed standard — so they stack as separate lines (each is its own question:
// "my goal" vs "the strain norm"). Both are ceilings (lower is better).
function MortalityBadges({
  actual,
  target,
  standard,
  standardWeek,
  standardSource,
}: {
  actual: number;
  target: number | null | undefined;
  standard: number | null | undefined;
  standardWeek: number | null | undefined;
  standardSource?: "breed" | "farm" | "default" | null;
}) {
  return (
    <>
      {target != null && (
        <TargetVarianceBadge
          actual={actual}
          target={target}
          metric="mortality_pct"
          className="block text-gray-500 dark:text-gray-400"
        />
      )}
      {/* `> 0` matches what the badge will actually render (targetVariance drops a
          ≤0 target), so a week-0 standard of 0% doesn't leave a dangling "(wk 0)". */}
      {standard != null && standard > 0 && (
        <span className="block text-gray-500 dark:text-gray-400">
          <TargetVarianceBadge actual={actual} target={standard} metric="mortality_pct" label={standardLabel(standardSource)} />
          {standardWeek != null && <span className="text-gray-400 dark:text-gray-500"> (wk {standardWeek})</span>}
        </span>
      )}
    </>
  );
}

function Metric({
  label,
  value,
  tone,
  badge,
  hint,
}: {
  label: string;
  value: string;
  tone?: "positive" | "negative";
  badge?: ReactNode;
  // A line under the value for what the number rests on, or — when there is
  // no number — what would produce one. "—" on its own teaches people the
  // metric is broken.
  hint?: string;
}) {
  const color =
    tone === "positive"
      ? "text-success-600 dark:text-success-500"
      : tone === "negative"
        ? "text-error-600 dark:text-error-500"
        : "text-gray-800 dark:text-white/90";
  return (
    <div>
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p className={`mt-0.5 text-lg font-semibold ${color}`}>{value}</p>
      {badge && <p className="mt-0.5 text-xs">{badge}</p>}
      {hint && <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">{hint}</p>}
    </div>
  );
}

// Fallback for a payload cached before slice 2c added `hdp` — degrade gracefully
// (no HDP banner / a "—" tile) instead of crashing on econ.hdp.current_pct.
const EMPTY_HDP = {
  current_pct: null,
  break_even_pct: null,
  producing_days: 0,
  through_date: null,
  standard_pct: null,
  standard_week: null,
  standard_source: null,
} as const;

// Per-flock cycle-to-date economics section for the Flock Detail page.
// Self-contained: loads its own data given a flockId.
export default function FlockEconomicsCard({ flockId }: { flockId: number }) {
  const [econ, setEcon] = useState<FlockEconomics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const targets = useAuthStore((s) => s.farm?.features?.targets);

  useEffect(() => {
    if (!flockId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    economicsService
      .getFlockEconomics(flockId)
      .then(setEcon)
      .catch((err) => setError(handleApiError(err)))
      .finally(() => setLoading(false));
  }, [flockId]);

  const hdp = econ?.hdp ?? EMPTY_HDP;

  return (
    <div className="mb-6 card p-5 sm:p-6">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-800 dark:text-white/90">
          Economics (cycle-to-date)
        </h3>
        {econ && (
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {econ.period.from} &ndash; {econ.period.to}
          </span>
        )}
      </div>

      {loading && <p className="py-6 text-center text-sm text-gray-400">Loading&hellip;</p>}
      {!loading && error && <p className="py-6 text-center text-sm text-error-500">{error}</p>}

      {!loading && !error && econ && (
        <>
          {econ.onboarded && (
            <div className="mb-4 rounded-lg bg-warning-50 px-3 py-2 text-xs text-warning-700 dark:bg-warning-500/10 dark:text-warning-400">
              Onboarded flock &mdash; showing <span className="font-medium">operating economics since adoption</span>.
              Rearing cost (chicks + pre-lay feed) predates the app and isn&rsquo;t captured, so this is not a full-cycle P&L.
            </div>
          )}

          {econ.flags.cost_data_incomplete && (
            <div className="mb-4 rounded-lg bg-warning-50 px-3 py-2 text-xs text-warning-700 dark:bg-warning-500/10 dark:text-warning-400">
              Cost data incomplete &mdash; feed is logged but its cost is ₹0, so cost/egg, margin and operating margin
              can&rsquo;t be computed. <span className="font-medium">Enter the rate on the purchases of its ingredients</span> (Purchases &rarr; Edit); feed already milled is then costed.
            </div>
          )}

          {/* Headline */}
          <div className="mb-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">Operating margin</p>
            <p
              className={`text-2xl font-bold ${
                econ.flags.cost_data_incomplete
                  ? "text-gray-400 dark:text-gray-500"
                  : econ.operating_margin >= 0
                    ? "text-success-600 dark:text-success-500"
                    : "text-error-600 dark:text-error-500"
              }`}
            >
              {econ.flags.cost_data_incomplete ? "—" : inr(econ.operating_margin)}
            </p>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Cost <span className="font-semibold">{inr2(econ.cost_per_egg)}</span>/egg
              {targets?.cost_per_egg != null && (
                <>
                  {" "}
                  <TargetVarianceBadge
                    actual={econ.cost_per_egg}
                    target={targets.cost_per_egg}
                    metric="cost_per_egg"
                    className="text-gray-500 dark:text-gray-400"
                  />
                </>
              )}
              {econ.egg_price_realized !== null && (
                <> &middot; realized <span className="font-semibold">{inr2(econ.egg_price_realized)}</span>/egg</>
              )}
              {econ.egg_price_realized !== null && econ.cost_per_egg !== null && (
                <MarginPerEgg realized={econ.egg_price_realized} cost={econ.cost_per_egg} />
              )}
            </p>
          </div>

          {hdp.current_pct !== null && (
            <HdpBanner
              current={hdp.current_pct}
              breakEven={hdp.break_even_pct}
              target={targets?.hdp_pct}
              standard={hdp.standard_pct}
              standardWeek={hdp.standard_week}
              standardSource={hdp.standard_source}
              producingDays={hdp.producing_days}
              throughDate={hdp.through_date}
              windowEnd={econ.period.to}
              feedEstimated={econ.flags.feed_cost_estimated}
              costIncomplete={econ.flags.cost_data_incomplete}
            />
          )}

          {/* Cost-driver waterfall — the visual "why" behind cost/egg */}
          <CostWaterfall econ={econ} />

          {/* Grid */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            <Metric label="Egg revenue" value={inr(econ.revenue.egg)} />
            <Metric label="Spent-hen revenue" value={inr(econ.revenue.bird)} />
            <Metric label="Manure revenue" value={inr(econ.revenue.manure)} />
            <Metric label="Total revenue" value={inr(econ.revenue.total)} />
            <Metric label="Feed cost" value={inr(econ.cost.feed)} />
            <Metric label="Chick cost" value={inr(econ.cost.chick)} />
            <Metric label="Overhead (allocated)" value={inr(econ.cost.overhead)} />
            <Metric label="Operating cost" value={inr(econ.cost.operating_total)} />
            <Metric
              label={`Feed / kg${econ.flags.feed_cost_estimated && !econ.flags.cost_data_incomplete ? " (est.)" : ""}`}
              value={inr2(econ.feed_cost_per_kg)}
            />
            <Metric label="Cost / surviving bird" value={inr2(econ.cost_per_surviving_bird)} />
            <Metric
              label="FCR"
              // `!= null` (not `!== null`) so a payload cached before this slice — where
              // fcr is `undefined`, not `null` — degrades to "—" instead of crashing on
              // `.toFixed`, matching the card's EMPTY_HDP cache-safety.
              value={econ.fcr != null ? econ.fcr.toFixed(2) : "—"}
              hint={
                econ.fcr != null
                  ? econ.fcr_basis_egg_weight_g != null
                    ? `at ${econ.fcr_basis_egg_weight_g.toFixed(1)} g/egg`
                    : undefined
                  : // fcr is null for THREE reasons — no eggs, no feed
                    // attributed, or no egg weight — and only the LAST is
                    // fixed by weighing. Both other gates are needed: without
                    // the eggs one, a pre-lay flock (every flock for its first
                    // eighteen weeks) is told to weigh eggs it has not got;
                    // without the feed one, a farm buying compounded feed
                    // rather than milling is sent to weigh a tray that will
                    // not give them a ratio either.
                    //
                    // The alert (alert_service.go, fcr_unmeasurable) applies
                    // these two and two more: it needs an FCR ceiling to be
                    // configured, and it stops asking after a while. The card
                    // deliberately does not — this is a caption on a dash the
                    // reader is already looking at, not a notification, so it
                    // is useful to a farm that set no ceiling and to a flock
                    // a year into lay. The two gates that matter are the ones
                    // about whether weighing would HELP; those must agree, and
                    // do.
                    econ.eggs_produced > 0 && econ.feed_produced_kg > 0
                    ? "Weigh a tray of eggs to see this"
                    : undefined
              }
              badge={
                // Guard on the actual too: a bare TargetVarianceBadge renders null when
                // the actual is null, but the Metric badge slot would still emit an empty
                // spacer line under a "—" tile — so skip it entirely.
                econ.fcr != null && targets?.fcr != null && (
                  <TargetVarianceBadge
                    actual={econ.fcr}
                    target={targets.fcr}
                    metric="fcr"
                    className="text-gray-500 dark:text-gray-400"
                  />
                )
              }
            />
            <Metric
              label="Mortality"
              value={econ.mortality_pct != null ? `${econ.mortality_pct.toFixed(1)}%` : "—"}
              badge={
                econ.mortality_pct != null &&
                (targets?.mortality_pct != null || econ.mortality_standard_pct != null) && (
                  <MortalityBadges
                    actual={econ.mortality_pct}
                    target={targets?.mortality_pct}
                    standard={econ.mortality_standard_pct}
                    standardWeek={econ.mortality_standard_week}
                    standardSource={econ.mortality_standard_source}
                  />
                )
              }
            />
            <Metric label="Eggs produced" value={econ.eggs_produced.toLocaleString("en-IN")} />
            <Metric label="Current birds" value={econ.current_birds.toLocaleString("en-IN")} />
            <Metric label="Hen-days" value={econ.hen_days.toLocaleString("en-IN")} />
            <Metric label="Laying hen-days" value={econ.laying_hen_days.toLocaleString("en-IN")} />
            <Metric
              label="HDP% (producing days)"
              value={hdp.current_pct !== null ? `${hdp.current_pct.toFixed(1)}%` : "—"}
            />
          </div>

          <p className="mt-4 text-xs text-gray-400 dark:text-gray-500">
            Margin/egg is after <span className="font-medium">all</span> costs incl. rearing; break-even HDP covers
            feed&nbsp;+&nbsp;overhead only, since rearing is already sunk. HDP is over days with egg logs, so it can
            differ from total laying hen-days. Overhead is allocated by this flock&rsquo;s share of farm bird-days.
            Still excludes depreciation and financing (not yet captured). Feed = feed ingredients only. Medicine,
            trays and other non-feed materials are not included here &mdash; a bill is not for one flock &mdash; so
            the farm&rsquo;s margin (Farm P&amp;L) can be lower than its flocks add up to.
          </p>
        </>
      )}
    </div>
  );
}
